import type { DashboardAccountInfo } from './accounts'

export type NetWorthEntry = {
  label: string
  value: number
  addToTotal: boolean
}

export type AccountBalanceInput = {
  accountType?: string | null
  accountSubType?: string | null
  balances?: {
    available?: number | null
    current?: number | null
  } | null
}

export type AccountBalanceView = {
  displayValue: number
  displayLabel: string
  addToTotal: boolean
}

type BalanceRule = {
  balanceField: 'available' | 'current'
  addToTotal: boolean
}

const PLAID_ACCOUNT_TYPES = new Set([
  'depository',
  'credit',
  'loan',
  'investment',
  'brokerage',
  'payroll',
  'other',
])

const FINASTRA_TYPE_MAP: Record<string, { accountType: string; accountSubType: string }> = {
  CHK: { accountType: 'depository', accountSubType: 'checking' },
  SAV: { accountType: 'depository', accountSubType: 'savings' },
  MM: { accountType: 'depository', accountSubType: 'money market' },
  CD: { accountType: 'depository', accountSubType: 'cd' },
  CARD: { accountType: 'credit', accountSubType: 'credit card' },
  LOC: { accountType: 'loan', accountSubType: 'line of credit' },
  LOAN: { accountType: 'loan', accountSubType: 'loan' },
  NONE: { accountType: 'other', accountSubType: 'other' },
}

const ACCOUNT_RULES: Record<string, Record<string, BalanceRule>> = {
  depository: {
    'cash management': { balanceField: 'available', addToTotal: true },
    cd: { balanceField: 'current', addToTotal: true },
    checking: { balanceField: 'available', addToTotal: true },
    ebt: { balanceField: 'available', addToTotal: true },
    hsa: { balanceField: 'current', addToTotal: true },
    'money market': { balanceField: 'current', addToTotal: true },
    paypal: { balanceField: 'available', addToTotal: true },
    prepaid: { balanceField: 'available', addToTotal: true },
    savings: { balanceField: 'current', addToTotal: true },
  },
  credit: {
    'credit card': { balanceField: 'current', addToTotal: false },
    paypal: { balanceField: 'current', addToTotal: false },
  },
  loan: {
    auto: { balanceField: 'current', addToTotal: false },
    business: { balanceField: 'current', addToTotal: false },
    commercial: { balanceField: 'current', addToTotal: false },
    construction: { balanceField: 'current', addToTotal: false },
    consumer: { balanceField: 'current', addToTotal: false },
    'home equity': { balanceField: 'current', addToTotal: false },
    'line of credit': { balanceField: 'current', addToTotal: false },
    loan: { balanceField: 'current', addToTotal: false },
    mortgage: { balanceField: 'current', addToTotal: false },
    other: { balanceField: 'current', addToTotal: false },
    overdraft: { balanceField: 'current', addToTotal: false },
    student: { balanceField: 'current', addToTotal: false },
  },
  investment: {
    '529': { balanceField: 'current', addToTotal: true },
    '401a': { balanceField: 'current', addToTotal: true },
    '401k': { balanceField: 'current', addToTotal: true },
    '403b': { balanceField: 'current', addToTotal: true },
    '457b': { balanceField: 'current', addToTotal: true },
    brokerage: { balanceField: 'current', addToTotal: true },
    'cash isa': { balanceField: 'current', addToTotal: true },
    'crypto exchange': { balanceField: 'current', addToTotal: true },
    'education savings account': { balanceField: 'current', addToTotal: true },
    'fixed annuity': { balanceField: 'current', addToTotal: true },
    gic: { balanceField: 'current', addToTotal: true },
    'health reimbursement arrangement': { balanceField: 'current', addToTotal: true },
    hsa: { balanceField: 'current', addToTotal: true },
    isa: { balanceField: 'current', addToTotal: true },
    ira: { balanceField: 'current', addToTotal: true },
    keogh: { balanceField: 'current', addToTotal: true },
    lif: { balanceField: 'current', addToTotal: true },
    'life insurance': { balanceField: 'current', addToTotal: true },
    lira: { balanceField: 'current', addToTotal: true },
    lrif: { balanceField: 'current', addToTotal: true },
    lrsp: { balanceField: 'current', addToTotal: true },
    'mutual fund': { balanceField: 'current', addToTotal: true },
    'non-custodial wallet': { balanceField: 'current', addToTotal: true },
    'non-taxable brokerage account': { balanceField: 'current', addToTotal: true },
    'other annuity': { balanceField: 'current', addToTotal: true },
    'other insurance': { balanceField: 'current', addToTotal: true },
    pension: { balanceField: 'current', addToTotal: true },
    prif: { balanceField: 'current', addToTotal: true },
    'profit sharing plan': { balanceField: 'current', addToTotal: true },
    qshr: { balanceField: 'current', addToTotal: true },
    rdsp: { balanceField: 'current', addToTotal: true },
    resp: { balanceField: 'current', addToTotal: true },
    retirement: { balanceField: 'current', addToTotal: true },
    rlif: { balanceField: 'current', addToTotal: true },
    roth: { balanceField: 'current', addToTotal: true },
    'roth 401k': { balanceField: 'current', addToTotal: true },
    rrif: { balanceField: 'current', addToTotal: true },
    rrsp: { balanceField: 'current', addToTotal: true },
    sarsep: { balanceField: 'current', addToTotal: true },
    'sep ira': { balanceField: 'current', addToTotal: true },
    'simple ira': { balanceField: 'current', addToTotal: true },
    sipp: { balanceField: 'current', addToTotal: true },
    'stock plan': { balanceField: 'current', addToTotal: true },
    student: { balanceField: 'current', addToTotal: true },
    'thrift savings plan': { balanceField: 'current', addToTotal: true },
    tfsa: { balanceField: 'current', addToTotal: true },
    trust: { balanceField: 'current', addToTotal: true },
    ugma: { balanceField: 'current', addToTotal: true },
    utma: { balanceField: 'current', addToTotal: true },
    'variable annuity': { balanceField: 'current', addToTotal: true },
    other: { balanceField: 'current', addToTotal: true },
  },
  brokerage: {
    default: { balanceField: 'current', addToTotal: true },
  },
  payroll: {
    payroll: { balanceField: 'available', addToTotal: true },
  },
  other: {
    other: { balanceField: 'current', addToTotal: false },
  },
}

export const normalizeAccountTypesForBalance = (
  accountType?: string | null,
  accountSubType?: string | null
): { accountType: string; accountSubType: string } => {
  const rawType = (accountType || 'other').trim()
  const rawSubtype = (accountSubType || 'other').trim()
  const typeLower = rawType.toLowerCase()

  if (PLAID_ACCOUNT_TYPES.has(typeLower)) {
    return {
      accountType: typeLower,
      accountSubType: rawSubtype.toLowerCase(),
    }
  }

  const finastraKey = rawType.toUpperCase()
  const mapped = FINASTRA_TYPE_MAP[finastraKey]
  if (mapped) {
    return mapped
  }

  return {
    accountType: typeLower || 'other',
    accountSubType: rawSubtype.toLowerCase() || 'other',
  }
}

const resolveNetWorthEntry = (
  accountType: string,
  accountSubType: string,
  balances: { available?: number | null; current?: number | null }
): NetWorthEntry => {
  const typeRules = ACCOUNT_RULES[accountType]
  let rule = typeRules?.[accountSubType] ?? typeRules?.default ?? null

  if (!rule) {
    if (accountType === 'depository') {
      rule = { balanceField: 'available', addToTotal: true }
    } else if (accountType === 'investment' || accountType === 'brokerage') {
      rule = { balanceField: 'current', addToTotal: true }
    } else if (accountType === 'credit' || accountType === 'loan') {
      rule = { balanceField: 'current', addToTotal: false }
    } else {
      rule = { balanceField: 'current', addToTotal: false }
    }
  }

  let usedField = rule.balanceField
  let rawValue = balances[usedField]

  if (rawValue == null) {
    const other = usedField === 'available' ? 'current' : 'available'
    rawValue = balances[other]
    usedField = other
  }

  if (rawValue == null) {
    rawValue = 0
  }

  let addToTotal = Boolean(rule.addToTotal)
  if ((accountType === 'credit' || accountType === 'loan') && !rule.addToTotal) {
    const current = balances.current
    if (typeof current === 'number' && current < 0) {
      addToTotal = true
      rawValue = Math.abs(current)
      usedField = 'current'
    }
  }

  const label = usedField === 'available' ? 'Available Balance' : 'Balance'

  return { label, value: Number(rawValue), addToTotal }
}

export const resolveAccountBalanceView = (input: AccountBalanceInput): AccountBalanceView => {
  const { accountType, accountSubType } = normalizeAccountTypesForBalance(
    input.accountType,
    input.accountSubType
  )
  const balances = input.balances || { available: null, current: null }
  const entry = resolveNetWorthEntry(accountType, accountSubType, balances)

  return {
    displayValue: entry.value,
    displayLabel: entry.label,
    addToTotal: entry.addToTotal,
  }
}

export const mapPlaidAccountToNetWorthEntry = (account: DashboardAccountInfo): NetWorthEntry => {
  const { accountType, accountSubType } = normalizeAccountTypesForBalance(
    account.accountType,
    account.accountSubType
  )
  const balances = account.balances || { available: null, current: null }

  return resolveNetWorthEntry(accountType, accountSubType, balances)
}

export const aggregateInstitutionBalance = (accounts: DashboardAccountInfo[]): number => {
  return accounts.reduce((total, account) => {
    const entry = mapPlaidAccountToNetWorthEntry(account)
    return total + (entry.addToTotal ? entry.value : 0)
  }, 0)
}

export const aggregateBalanceViews = (
  views: Array<Pick<AccountBalanceView, 'displayValue' | 'addToTotal'>>
): number | null => {
  let sum = 0
  let hasAny = false

  for (const view of views) {
    if (view.addToTotal) {
      sum += view.displayValue
      hasAny = true
    }
  }

  return hasAny ? sum : null
}
